// Sprint runner-claude-version-visibility — minimal semver comparison.
//
// New (verified no equivalent exists in web/src — grep for `semverLt` /
// `parseSemver` / `compareVersion` returned nothing). Deliberately tiny:
// the only consumer is the UsageModal's "outdated" badge, which asks one
// question — is runner X behind the fleet maximum? No range/caret/tilde
// parsing, no pre-release handling; `claude --version` emits a plain
// `X.Y.Z` triple and nothing else.

// Parses a leading `X.Y.Z` into a numeric tuple, or null if the string is
// not a dotted-triple. Tolerates a trailing suffix (` (Claude Code)`) but
// the producer already strips that; this is defence in depth.
export function parseSemver(v: string | null | undefined): [number, number, number] | null {
  if (!v) return null;
  const m = v.trim().match(/^(\d+)\.(\d+)\.(\d+)/);
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

// True iff `a` is a strictly-lower version than `b`. Returns FALSE whenever
// either side is null/unparseable — the caller uses this to fire an
// "outdated" badge, and an unknown version must NEVER badge as outdated
// (spec AC-3/AC-4: a runner that never reported shows a neutral "—",
// not a false warning).
export function semverLt(a: string | null | undefined, b: string | null | undefined): boolean {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  if (pa === null || pb === null) return false;
  for (let i = 0; i < 3; i += 1) {
    if (pa[i]! < pb[i]!) return true;
    if (pa[i]! > pb[i]!) return false;
  }
  return false; // equal
}

// The highest version across a set (the fleet maximum, the MVP drift
// reference per spec §6). Ignores null/unparseable entries. Returns null
// when no entry carries a parseable version (→ nothing badges).
export function fleetMaxVersion(versions: Array<string | null | undefined>): string | null {
  let max: string | null = null;
  for (const v of versions) {
    if (parseSemver(v) === null) continue;
    if (max === null || semverLt(max, v)) max = v as string;
  }
  return max;
}
